/**
 * BulkAssignBar — bottom selection bar for bulk-assign flow.
 *
 * Phase 9 / Plan 09-04 — shows when the dispatcher selects ≥1 rows in JobQueue.
 * "N jobs selected — [Assign to Driver ▾] [Cancel]"
 * "Assign to Driver" opens a shadcn Command palette populated from GET /v1/drivers
 * (the FULL active roster — includes idle drivers with no current jobs, Blocker 4).
 * On confirm: POST /v1/jobs/bulk-assign → invalidate jobs query → dismiss bar.
 *
 * CTA copy: "Assign to Driver" (UI-SPEC exact).
 * Error copy: "Assignment failed. Check driver availability and try again."
 *
 * Spec: UI-SPEC §3 (bulk-assign mechanic); Copywriting; DISPATCH-01; T-09-04-01.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useApiOpts, useApiMutation } from '@lodex-pro/web-shared';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@lodex-pro/web-shared/src/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@lodex-pro/web-shared/src/components/ui/popover';
import { Button } from '@lodex-pro/web-shared/src/components/ui/button';
import { useDrivers } from './use-drivers';
import { JOBS_QUERY_KEY } from './use-jobs';

interface BulkAssignBody {
  readonly jobIds: readonly string[];
  readonly targetDriverId: string;
  readonly note?: string;
}

interface BulkAssignResponse {
  readonly ok: boolean;
  readonly assigned: number;
  readonly failed: ReadonlyArray<{ readonly id: string; readonly reason: string }>;
}

interface BulkAssignBarProps {
  /** IDs of the currently-selected jobs. */
  readonly selectedJobIds: readonly string[];
  /** Called when the dispatcher cancels the selection. */
  readonly onCancel: () => void;
  /** Called after a successful bulk-assign (to clear selection). */
  readonly onSuccess: () => void;
}

export function BulkAssignBar({
  selectedJobIds,
  onCancel,
  onSuccess,
}: BulkAssignBarProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();

  const { data: driversData } = useDrivers(apiOpts);
  const drivers = React.useMemo(() => driversData?.drivers ?? [], [driversData]);

  const [open, setOpen] = React.useState(false);
  const [selectedDriverId, setSelectedDriverId] = React.useState<string | null>(null);
  const [driverSearch, setDriverSearch] = React.useState('');

  const selectedDriverName = React.useMemo(() => {
    if (selectedDriverId === null) return null;
    return drivers.find((d) => d.driverId === selectedDriverId)?.driverName ?? null;
  }, [selectedDriverId, drivers]);

  const mutation = useApiMutation<BulkAssignBody, BulkAssignResponse>(
    apiOpts,
    '/v1/jobs/bulk-assign',
    'POST',
    {
      onSuccess: (result) => {
        if (result.assigned > 0) {
          toast.success(
            `${result.assigned.toString()} job${result.assigned === 1 ? '' : 's'} assigned successfully.`,
          );
        }
        if (result.failed.length > 0) {
          toast.error(
            `${result.failed.length.toString()} job${result.failed.length === 1 ? '' : 's'} could not be assigned.`,
          );
        }
        void queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY({}) });
        onSuccess();
      },
      onError: () => {
        toast.error('Assignment failed. Check driver availability and try again.');
      },
    },
  );

  const handleAssign = (): void => {
    if (selectedDriverId === null) return;
    mutation.mutate({
      jobIds: selectedJobIds,
      targetDriverId: selectedDriverId,
    });
  };

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: 'var(--color-bg-secondary)',
        borderTop: '1px solid var(--color-border-primary)',
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.3)',
        zIndex: 10,
      }}
    >
      <span style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>
        {selectedJobIds.length} job{selectedJobIds.length === 1 ? '' : 's'} selected
      </span>

      {/* Driver picker — Command palette sourced from GET /v1/drivers (Blocker 4) */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Assign to Driver"
            style={{ minWidth: '180px', justifyContent: 'space-between' }}
          >
            {selectedDriverName ?? 'Assign to Driver'}
            <ChevronsUpDown
              size={14}
              aria-hidden="true"
              style={{ marginLeft: '4px', flexShrink: 0 }}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent style={{ width: '240px', padding: 0 }}>
          <Command>
            <CommandInput
              placeholder="Search drivers…"
              value={driverSearch}
              onValueChange={setDriverSearch}
            />
            <CommandList>
              <CommandEmpty>No drivers found.</CommandEmpty>
              <CommandGroup>
                {drivers.map((driver) => (
                  <CommandItem
                    key={driver.driverId}
                    value={driver.driverName}
                    onSelect={(): void => {
                      setSelectedDriverId(driver.driverId);
                      setDriverSearch('');
                      setOpen(false);
                    }}
                  >
                    <Check
                      size={14}
                      aria-hidden="true"
                      style={{
                        marginRight: '8px',
                        opacity: selectedDriverId === driver.driverId ? 1 : 0,
                      }}
                    />
                    {driver.driverName}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Primary CTA */}
      <Button
        onClick={handleAssign}
        disabled={selectedDriverId === null || mutation.isPending}
        style={{
          backgroundColor: 'var(--color-accent-blue)',
          color: 'var(--color-on-accent)',
          border: 'none',
        }}
      >
        {mutation.isPending ? 'Assigning…' : 'Assign to Driver'}
      </Button>

      {/* Cancel */}
      <Button variant="ghost" onClick={onCancel} disabled={mutation.isPending}>
        Cancel
      </Button>
    </div>
  );
}
